import React, { useState, useEffect } from 'react';
import UserLayout from '../../components/layouts/UserLayout';
import { useAuth } from '../../context/AuthContext';
import { userAPI, authAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  User, 
  Mail, 
  Phone, 
  Building2, 
  Lock, 
  Save, 
  Loader2,
  Eye,
  EyeOff,
  CheckCircle,
  Shield,
  Smartphone,
  ShieldCheck,
  ShieldOff,
  QrCode,
  KeyRound,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const UserProfile = () => {
  const { user: authUser, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Profile form state
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    company_name: '',
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState(false);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FADisable, setShow2FADisable] = useState(false);
  const [setting2FA, setSetting2FA] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [manualSecret, setManualSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disablePassword, setDisablePassword] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await userAPI.getProfile();
      setProfile({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        company_name: res.data.company_name || '',
      });
      setTwoFactorEnabled(res.data.totp_enabled || false);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  // 2FA Setup Functions
  const handleSetup2FA = async () => {
    setSetting2FA(true);
    try {
      const response = await authAPI.setup2FA();
      setQrCodeData(response.data.qr_code_url);
      setManualSecret(response.data.manual_entry_key);
      setShow2FASetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setSetting2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying2FA(true);
    try {
      await authAPI.verify2FA(verificationCode);
      setTwoFactorEnabled(true);
      setShow2FASetup(false);
      setVerificationCode('');
      setQrCodeData(null);
      setManualSecret('');
      updateUser({ totp_enabled: true });
      toast.success('Two-factor authentication enabled successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!disablePassword) {
      toast.error('Please enter your password');
      return;
    }
    if (disableCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setDisabling2FA(true);
    try {
      await authAPI.disable2FA(disablePassword, disableCode);
      setTwoFactorEnabled(false);
      setShow2FADisable(false);
      setDisableCode('');
      setDisablePassword('');
      updateUser({ totp_enabled: false });
      toast.success('Two-factor authentication has been disabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setDisabling2FA(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await userAPI.updateProfile({
        name: profile.name,
        phone: profile.phone,
        company_name: profile.company_name,
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const validatePassword = (pwd) => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(pwd)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number';
    return null;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Validate new password
    const passwordError = validatePassword(passwordForm.newPassword);
    if (passwordError) {
      toast.error(passwordError);
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await userAPI.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <UserLayout title="My Profile">
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout title="My Profile" subtitle="Manage your account settings">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  Full Name
                </Label>
                <Input
                  id="name"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  placeholder="Your full name"
                  data-testid="profile-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  value={profile.email}
                  disabled
                  className="bg-slate-50"
                  data-testid="profile-email"
                />
                <p className="text-xs text-slate-500">Email cannot be changed. Contact admin for assistance.</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                  data-testid="profile-phone"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  Company Name
                </Label>
                <Input
                  id="company"
                  value={profile.company_name}
                  onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  placeholder="Your company name"
                  data-testid="profile-company"
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  Current Password
                </Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                    placeholder="Enter current password"
                    required
                    data-testid="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Enter new password"
                  required
                  data-testid="new-password"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password"
                />
              </div>
              
              {/* Password Requirements */}
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-medium text-slate-700 mb-2">Password must contain:</p>
                <ul className="space-y-1 text-slate-600">
                  <li className={passwordForm.newPassword.length >= 8 ? 'text-green-600' : ''}>
                    {passwordForm.newPassword.length >= 8 ? <CheckCircle className="w-3 h-3 inline mr-1" /> : '○'} At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(passwordForm.newPassword) ? 'text-green-600' : ''}>
                    {/[A-Z]/.test(passwordForm.newPassword) ? <CheckCircle className="w-3 h-3 inline mr-1" /> : '○'} One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(passwordForm.newPassword) ? 'text-green-600' : ''}>
                    {/[a-z]/.test(passwordForm.newPassword) ? <CheckCircle className="w-3 h-3 inline mr-1" /> : '○'} One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(passwordForm.newPassword) ? 'text-green-600' : ''}>
                    {/[0-9]/.test(passwordForm.newPassword) ? <CheckCircle className="w-3 h-3 inline mr-1" /> : '○'} One number
                  </li>
                </ul>
              </div>
              
              <Button 
                type="submit" 
                variant="outline"
                className="w-full"
                disabled={changingPassword}
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-indigo-600" />
              Two-Factor Authentication (2FA)
            </CardTitle>
            <CardDescription>
              Add an extra layer of security to your account by requiring a verification code from your authenticator app when signing in.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {twoFactorEnabled ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <ShieldCheck className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">2FA is Enabled</p>
                    <p className="text-sm text-green-700">Your account is protected with two-factor authentication.</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => setShow2FADisable(true)}
                  data-testid="disable-2fa-btn"
                >
                  <ShieldOff className="w-4 h-4 mr-2" />
                  Disable Two-Factor Authentication
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-8 h-8 text-amber-600" />
                  <div>
                    <p className="font-semibold text-amber-800">2FA is Not Enabled</p>
                    <p className="text-sm text-amber-700">Enable two-factor authentication to secure your account.</p>
                  </div>
                </div>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={handleSetup2FA}
                  disabled={setting2FA}
                  data-testid="enable-2fa-btn"
                >
                  {setting2FA ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Enable Two-Factor Authentication
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* 2FA Setup Dialog */}
      <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-indigo-600" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* QR Code */}
            {qrCodeData && (
              <div className="flex justify-center">
                <div className="p-4 bg-white border-2 border-slate-200 rounded-lg">
                  <img 
                    src={qrCodeData} 
                    alt="2FA QR Code" 
                    className="w-48 h-48"
                    data-testid="2fa-qr-code"
                  />
                </div>
              </div>
            )}
            
            {/* Manual Entry */}
            <div className="space-y-2">
              <p className="text-sm text-slate-600 font-medium">Can't scan? Enter this code manually:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-slate-100 rounded text-sm font-mono break-all" data-testid="2fa-secret">
                  {manualSecret}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(manualSecret);
                    toast.success('Secret copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
            
            {/* Verification */}
            <div className="space-y-2">
              <Label htmlFor="verification-code" className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-slate-400" />
                Enter Verification Code
              </Label>
              <Input
                id="verification-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
                data-testid="2fa-verification-code"
              />
              <p className="text-xs text-slate-500">Enter the 6-digit code from your authenticator app</p>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShow2FASetup(false);
                  setVerificationCode('');
                  setQrCodeData(null);
                  setManualSecret('');
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                onClick={handleVerify2FA}
                disabled={verifying2FA || verificationCode.length !== 6}
                data-testid="verify-2fa-btn"
              >
                {verifying2FA ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Verify & Enable
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2FA Disable Dialog */}
      <AlertDialog open={show2FADisable} onOpenChange={setShow2FADisable}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldOff className="w-5 h-5" />
              Disable Two-Factor Authentication
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the extra security layer from your account. Enter your password and the verification code from your authenticator app to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="disable-password" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-400" />
                Password
              </Label>
              <Input
                id="disable-password"
                type="password"
                placeholder="Enter your password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                data-testid="disable-2fa-password"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="disable-code" className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-slate-400" />
                Verification Code
              </Label>
              <Input
                id="disable-code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest font-mono"
                data-testid="disable-2fa-code"
              />
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setDisableCode(''); setDisablePassword(''); }}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDisable2FA}
              disabled={disabling2FA || disableCode.length !== 6 || !disablePassword}
              data-testid="confirm-disable-2fa-btn"
            >
              {disabling2FA ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable 2FA'
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UserLayout>
  );
};

export default UserProfile;
